<template>
  <div class="flex items-center gap-2 p-2 bg-slate-800/40 rounded">
    <span v-if="issues.length" class="shrink-0 cursor-help" :title="issues.map(i => i.message).join('\n')">
      {{ hasError ? '❌' : '⚠' }}
    </span>
    <span v-else class="shrink-0 w-[1em]"></span>

    <div class="flex flex-col gap-0.5 shrink-0">
      <button type="button" class="text-gray-400 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed leading-none"
        :disabled="!canMoveUp" title="Move up" @click="$emit('move-up')">
        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M10 5l-6 7h12z" /></svg>
      </button>
      <button type="button" class="text-gray-400 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed leading-none"
        :disabled="!canMoveDown" title="Move down" @click="$emit('move-down')">
        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M10 15l6-7H4z" /></svg>
      </button>
    </div>

    <template v-if="rule.when">
      <BuilderSelect class="w-20 text-sm px-1 py-1 shrink-0" v-model="rule.when.operator">
        <option v-for="op in OPERATORS" :key="op" :value="op">{{ op }}</option>
      </BuilderSelect>
      <BuilderInput type="text" placeholder="value" v-model="rule.when.value" class="w-20 text-sm px-1 py-1 shrink-0" />
    </template>
    <span v-else class="text-xs italic text-gray-400 shrink-0 w-[calc(3rem+5rem+0.5rem)]">always (default)</span>

    <span class="shrink-0 text-gray-500 select-none" aria-hidden="true">&rarr;</span>

    <PropertyEditorFactory class="flex-1 min-w-[8rem]" placeholder="result"
      stringBuilder :builderVariables="builderVariables" :valueExpression="valueExpression"
      :editorMeta="editorMeta" :modelValue="rule.result"
      @update:modelValue="v => rule.result = v" />

    <BuilderCheckbox v-if="canBeDefault" label="Default"
      class="text-xs text-gray-400 shrink-0 whitespace-nowrap"
      :modelValue="rule.when === null" @update:modelValue="onDefaultToggle" />

    <button type="button" class="text-red-400 hover:text-red-300 shrink-0" title="Remove rule" @click="$emit('remove')">
      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
</template>

<script setup>
/**
 * MappingRuleRow.vue
 *
 * One row of a 'mapping' binding's rules[] — operator + comparison value
 * (or "always" when it's the default rule), plus the result editor. Used
 * only by MappingBindingEditor.vue. Mirrors ConditionEditor.vue's role for
 * trigger conditions: one small dedicated component per row, composing
 * PropertyEditorFactory for the one scalar value that's actually typed by
 * the bound widget property.
 *
 * Laid out as two visibly separated halves — `[operator] [condition
 * value] → [result]`. The condition half always compares the bound
 * entity's own value; the result half is typed by the *target property*
 * via PropertyEditorFactory + editorMeta (text input for a string
 * property such as label.text/badge.value_str, number input for x/y/
 * value), so one rule list serves both without a separate string-mapping
 * component. `stringBuilder` is passed unconditionally: it only takes
 * effect on a free-text editor, so the schema still decides whether a
 * result gets the String Builder — a numeric result keeps its plain
 * number input. The result editor keeps a min-width rather than `min-w-0`:
 * every sibling in the row is `shrink-0`, so a flex-basis-0 result field
 * is the only thing that can collapse when the row is cramped.
 *
 * `issues` (this rule's slice of validateMappingRules.js's output, sliced
 * by the parent) is display-only here — this component never calls the
 * validator itself, it only renders whatever issues it was handed.
 *
 * Uses BuilderSelect/BuilderInput/BuilderCheckbox, not the global
 * form-select/form-input/form-checkbox — those are styled for the light
 * property-panel forms; see BuilderSelect.vue's doc comment.
 */
import { computed } from 'vue';
import PropertyEditorFactory from './PropertyEditorFactory.vue';
import BuilderSelect from './BuilderSelect.vue';
import BuilderInput from './BuilderInput.vue';
import BuilderCheckbox from './BuilderCheckbox.vue';

const OPERATORS = ['==', '!=', '<', '<=', '>', '>='];

const props = defineProps({
  rule:         { type: Object, required: true }, // { when: {operator,value}|null, result } — mutated directly
  editorMeta:   { type: Object, required: true },
  canBeDefault: { type: Boolean, default: false }, // only the last rule may become the default
  canMoveUp:    { type: Boolean, default: false },
  canMoveDown:  { type: Boolean, default: false },
  issues:       { type: Array, default: () => [] }, // ValidationIssue[] for this rule only
  // String Builder context for the result field, supplied by the parent
  // (which owns the binding) — see MappingBindingEditor.vue. Passed through
  // untouched; this row never derives either of them.
  builderVariables: { type: Array, default: () => [] },
  valueExpression:  { type: String, default: '' },
});
defineEmits(['move-up', 'move-down', 'remove']);

const hasError = computed(() => props.issues.some(i => i.severity === 'error'));

function onDefaultToggle(checked) {
  props.rule.when = checked ? null : { operator: '==', value: '' };
}
</script>
